import { Container, Sprite, Texture } from 'pixi.js-legacy';

interface BackgroundState {
    hue: number;
    angle: number;
}

const PODO_COLORS = ['#7c4de0', '#d14a88', '#1896c4', '#5eb3ba'];

/**
 * Interpolate between two hex colors
 */
function interpolateColor(color1: string, color2: string, t: number): number {
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);

    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);

    return (r << 16) | (g << 8) | b;
}

/**
 * PixiJS Background Renderer for menu screens
 * Creates animated gradient background with rotation
 */
export class BackgroundRenderer {
    private container: Container;
    private sprite: Sprite | null = null;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private texture: Texture | null = null;
    private lastHue: number = -1;

    constructor(container: Container) {
        this.container = container;

        // Create offscreen canvas for gradient - larger size to prevent banding
        this.canvas = document.createElement('canvas');
        this.canvas.width = 1024;
        this.canvas.height = 1024;
        this.ctx = this.canvas.getContext('2d', { alpha: false })!;
    }

    render(bgState: BackgroundState, canvasWidth: number, canvasHeight: number): void {
        const hue = ((bgState.hue % 360) + 360) % 360;

        // Only update texture if hue changed significantly
        const hueDelta = Math.abs(hue - this.lastHue);
        if (hueDelta > 1 || this.lastHue === -1) {
            this.updateGradientTexture(hue, canvasWidth, canvasHeight);
            this.lastHue = hue;
        }

        if (this.sprite) {
            // Update rotation
            this.sprite.rotation = bgState.angle;

            // Center the sprite
            this.sprite.position.set(canvasWidth / 2, canvasHeight / 2);
        }
    }

    private updateGradientTexture(hue: number, canvasWidth: number, canvasHeight: number): void {
        const colorIndex = Math.floor(hue / 90) % PODO_COLORS.length;
        const nextColorIndex = (colorIndex + 1) % PODO_COLORS.length;
        const t = (hue % 90) / 90;

        const color1 = interpolateColor(PODO_COLORS[colorIndex], PODO_COLORS[nextColorIndex], t);
        const color2Index = (colorIndex + 2) % PODO_COLORS.length;
        const color2 = interpolateColor(PODO_COLORS[nextColorIndex], PODO_COLORS[color2Index], t);

        // Draw gradient on canvas
        const gradient = this.ctx.createLinearGradient(0, 0, this.canvas.width, this.canvas.height);
        gradient.addColorStop(0, `#${color1.toString(16).padStart(6, '0')}`);
        gradient.addColorStop(1, `#${color2.toString(16).padStart(6, '0')}`);
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Update or create texture
        if (this.texture) {
            // v7: baseTexture.update()
            this.texture.baseTexture.update();
        } else {
            this.texture = Texture.from(this.canvas);
            // Use linear filtering to prevent banding artifacts (v7: SCALE_MODES.LINEAR = 1)
            this.texture.baseTexture.scaleMode = 1;

            // Create sprite from texture
            this.sprite = new Sprite(this.texture);
            this.sprite.anchor.set(0.5);

            // Scale to cover entire screen even when rotated
            const maxDim = Math.max(canvasWidth, canvasHeight) * 3;
            this.sprite.width = maxDim;
            this.sprite.height = maxDim;

            this.container.addChild(this.sprite);
        }
    }

    clear(): void {
        if (this.sprite) {
            this.container.removeChild(this.sprite);
            this.sprite.destroy();
            this.sprite = null;
        }
        if (this.texture) {
            this.texture.destroy(true);
            this.texture = null;
        }
        this.lastHue = -1;
    }

    destroy(): void {
        this.clear();
    }
}
